import { useEffect, useRef, useState } from 'react';

/**
 * "Don't build a panel nobody has opened yet."
 *
 * The in-table overlays (Table Chat, Table History) stay in the tree while
 * closed and hide themselves with `visibility: hidden`, so their whole DOM —
 * every chat message, every history row with its avatar — was being built on
 * every table switch, for a panel the player may never open. Switching tables
 * remounts the per-table subtree, so that cost is paid again per switch and
 * doubled while the carousel has both tables mounted.
 *
 * This hook defers the first build until the panel is actually opened, then
 * keeps it mounted for the rest of the table's life: open/close stays instant
 * and scroll position, draft text and unread state behave exactly as before.
 *
 * Returns whether the panel should render its contents.
 */
export const useLazyPanelMount = (isOpen: boolean): boolean => {
  const [hasOpened, setHasOpened] = useState(isOpen);
  // Mirror in a ref so the effect never re-sets state once it has flipped.
  const hasOpenedRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setHasOpened(true);
    }
  }, [isOpen]);

  return hasOpened;
};

export default useLazyPanelMount;
